import {
    MAX_ITEM_COUNT_FOR_EXPORT,
    MAX_PAGE_SIZE_FOR_COMPARISON,
    STATISTICS,
} from '@client/app/shared-modules/statistics/statistics.constant';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import type { StatisticsDownload } from '@client/app/modules/statistics/interfaces/statistics-download.interface';
import type {
    SiteAnalyticsDataRequest,
    SiteAnalyticsDataResult,
    SiteAnalyticsLinkCell,
    SiteAnalyticsScaffoldConfig,
    SiteAnalyticsTableRow,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';
import type {
    SiteAnalyticsPageDefinition,
    SiteAnalyticsPageDefinitionDeps,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-definition.type';

const MOST_DOWNLOADS_PAGES_CONFIG: SiteAnalyticsScaffoldConfig = {
    pageHeaderKey: 'siteAnalytics.mostDownloads.header',
    pageSubheaderKey: 'siteAnalytics.mostDownloads.subheader',
    tableKey: 'site-analytics-most-downloads',
    dataSource: 'mostDownloads',
    exportCategory: 'most_downloads',
    exportCategoryRefMaxItems: MAX_ITEM_COUNT_FOR_EXPORT,
    searchPlaceholderKey: 'siteAnalytics.scaffold.search.placeholder',
    emptyMessageKey: 'siteAnalytics.scaffold.table.emptyDownloadsMessage',
    emptySearchMessageKey: 'siteAnalytics.scaffold.table.emptySearchDownloadsMessage',
    tableCaptionKey: 'siteAnalytics.mostDownloads.table.caption',
    exportButtonLabelKey: 'siteAnalytics.scaffold.export.buttonLabel',
    dateRangeLabelKey: 'siteAnalytics.scaffold.dateRange.label',
    compareToggleLabelKey: 'siteAnalytics.scaffold.compare.toggleLabel',
    compareDateRangeLabelKey: 'siteAnalytics.scaffold.compare.dateRangeLabel',
    defaultSortBy: '',
    defaultSortDir: 'desc',
    comparisonMaxPageSize: MAX_PAGE_SIZE_FOR_COMPARISON,
    chart: {
        titleKey: 'siteAnalytics.mostDownloads.chart.title',
        primarySeriesLabelKey: 'siteAnalytics.scaffold.chart.series.currentUniqueDownloads',
        comparisonSeriesLabelKey: 'siteAnalytics.scaffold.chart.series.comparisonUniqueDownloads',
    },
    columns: [
        {
            id: 'page',
            headerKey: 'siteAnalytics.scaffold.table.column.url',
            displayType: 'link',
            width: 'xl',
            ariaLabelKey: 'siteAnalytics.scaffold.table.urlAriaLabel{{url}}',
        },
        {
            id: 'uniqueDownloads',
            headerKey: 'siteAnalytics.scaffold.table.column.uniqueDownloads',
            displayType: 'number',
            width: 'md',
        },
        {
            id: 'uniqueDownloadsComparison',
            headerKey: 'siteAnalytics.scaffold.table.column.uniqueDownloadsComparison',
            displayType: 'number',
            visibility: 'comparison',
            width: 'md',
        },
        {
            id: 'downloads',
            headerKey: 'siteAnalytics.scaffold.table.column.downloads',
            displayType: 'number',
            width: 'md',
        },
        {
            id: 'downloadsComparison',
            headerKey: 'siteAnalytics.scaffold.table.column.downloadsComparison',
            displayType: 'number',
            visibility: 'comparison',
            width: 'md',
        },
    ],
};

const loadMostDownloadsData = async (
    request: SiteAnalyticsDataRequest,
    deps: SiteAnalyticsPageDefinitionDeps,
): Promise<SiteAnalyticsDataResult> => {
    const { statsRepoService, statisticsParamsService } = deps;
    const isComparisonMode = Boolean(request.comparisonDateRange);

    const currentParams = statisticsParamsService.buildListParams({
        limit: request.perPage,
        page: request.page,
        domainId: request.domainId,
        dateRange: request.dateRange,
        dateFormat: STATISTICS.DATE_FORMAT,
        sortBy: request.sortBy,
        sortDirection: request.sortDir,
        search: request.search,
    });

    if (!isComparisonMode || !request.comparisonDateRange) {
        const currentDownloads = await statsRepoService.getMostDownloads(
            currentParams,
            contextTokens.NO_PARSE_SEARCH,
        );

        const normalizedCurrentRows = normalizeRows(currentDownloads);

        return {
            rows: normalizedCurrentRows,
            chart: {
                labels: normalizedCurrentRows.map((row) => getPageLabel(row)),
                primaryValues: normalizedCurrentRows.map((row) => toInteger(row.uniqueDownloads)),
            },
            summary: null,
            total: getTotal(currentDownloads, normalizedCurrentRows.length),
            perPage: getPerPage(currentDownloads, request.perPage),
            page: getCurrentPage(currentDownloads, request.page),
        };
    }

    const comparisonParams = statisticsParamsService.buildComparisonParams(
        currentParams,
        request.comparisonDateRange,
        STATISTICS.DATE_FORMAT,
    );

    const [currentDownloadsResponse, comparisonDownloadsResponse] = await Promise.all([
        statsRepoService.getMostDownloads(currentParams, contextTokens.NO_PARSE_SEARCH),
        statsRepoService.getMostDownloads(comparisonParams, contextTokens.NO_PARSE_SEARCH),
    ]);

    const normalizedComparisonRows = normalizeRows(currentDownloadsResponse, comparisonDownloadsResponse);

    return {
        rows: normalizedComparisonRows,
        chart: {
            labels: normalizedComparisonRows.map((row) => getPageLabel(row)),
            primaryValues: normalizedComparisonRows.map((row) => toInteger(row.uniqueDownloads)),
            comparisonValues: normalizedComparisonRows.map((row) => toInteger(row.uniqueDownloadsComparison)),
        },
        summary: null,
        total: getTotal(currentDownloadsResponse, normalizedComparisonRows.length),
        perPage: getPerPage(currentDownloadsResponse, request.perPage),
        page: getCurrentPage(currentDownloadsResponse, request.page),
    };
};

function normalizeRows (
    currentDownloads: StatisticsDownload[],
    comparisonDownloads?: StatisticsDownload[],
): SiteAnalyticsTableRow[] {
    const comparisonByUrl = new Map<string, StatisticsDownload>();

    if (comparisonDownloads) {
        for (const download of comparisonDownloads) {
            const urlKey = String(download.url || '');
            if (!comparisonByUrl.has(urlKey)) {
                comparisonByUrl.set(urlKey, download);
            }
        }
    }

    return currentDownloads.map((download, index) => {
        const url = String(download.url || '');
        const comparisonDownload = comparisonByUrl.get(url);
        const title = String(download.title || '');

        return {
            id: `${url || 'row'}-${index}`,
            page: {
                label: url,
                href: url,
                ...(title && title !== url ? { tooltip: title } : {}),
            },
            uniqueDownloads: toInteger(download.downloads),
            uniqueDownloadsComparison: comparisonDownload ? toInteger(comparisonDownload.downloads) : null,
            downloads: toInteger(download.pageviews),
            downloadsComparison: comparisonDownload ? toInteger(comparisonDownload.pageviews) : null,
        };
    });
}

function getPageLabel (row: SiteAnalyticsTableRow): string {
    const linkCell = toLinkCell(row.page);
    if (linkCell) {
        return getUrlPath(linkCell.label);
    }

    if (typeof row.page === 'string') {
        return getUrlPath(row.page);
    }

    return '';
}

function getUrlPath (value: string): string {
    try {
        const url = new URL(value);

        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return value;
    }
}

function toLinkCell (value: unknown): SiteAnalyticsLinkCell | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const link = value as { label?: unknown; href?: unknown; tooltip?: unknown };
    if (typeof link.label !== 'string' || typeof link.href !== 'string') {
        return null;
    }

    return {
        label: link.label,
        href: link.href,
        ...(typeof link.tooltip === 'string' && link.tooltip ? { tooltip: link.tooltip } : {}),
    };
}

function toInteger (value: unknown): number {
    const parsed = Number.parseInt(String(value ?? 0), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
}

function getTotal (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'total' in collection) {
        const total = Number((collection as { total?: unknown }).total);
        if (!Number.isNaN(total) && total > 0) {
            return total;
        }
    }

    return fallback;
}

function getPerPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'perPage' in collection) {
        const perPage = Number((collection as { perPage?: unknown }).perPage);
        if (!Number.isNaN(perPage) && perPage > 0) {
            return perPage;
        }
    }

    return fallback;
}

function getCurrentPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'currentPage' in collection) {
        const currentPage = Number((collection as { currentPage?: unknown }).currentPage);
        if (!Number.isNaN(currentPage) && currentPage > 0) {
            return currentPage;
        }
    }

    return fallback;
}

export const MOST_DOWNLOADS_PAGES_DEFINITION: SiteAnalyticsPageDefinition = {
    key: 'mostDownloads',
    config: MOST_DOWNLOADS_PAGES_CONFIG,
    loadData: loadMostDownloadsData,
};

export const MOST_DOWNLOADS_PAGE_CONFIG = MOST_DOWNLOADS_PAGES_DEFINITION.config;
